import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { db } from '../config/firebase';
import { collection, addDoc, Timestamp } from 'firebase/firestore';
import './Commission.css';

const unitLogo = '/images/LP_Unit_logo.png';

const BASE_PRICE = 15;
const PREMIUM_ASSET_PRICE = 10;
const ACEX_PRICE = 10;

function VestRevision() {
  const { user, userData, logout } = useAuth();
  const [showInfoPopup, setShowInfoPopup] = useState(true);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // User info from popup
  const [zap, setZap] = useState('');
  const [discordName, setDiscordName] = useState('');
  const [paypalEmail, setPaypalEmail] = useState('');

  // Form fields - separate vest and belt
  const [vestDetails, setVestDetails] = useState('');
  const [beltDetails, setBeltDetails] = useState('');
  const [premiumAsset, setPremiumAsset] = useState(false);
  const [customAcex, setCustomAcex] = useState(false);

  const handleInfoSubmit = (e) => {
    e.preventDefault();
    if (zap && discordName && paypalEmail) {
      setShowInfoPopup(false);
    }
  };

  const calculateTotal = () => {
    let total = BASE_PRICE;
    if (premiumAsset) total += PREMIUM_ASSET_PRICE;
    if (customAcex) total += ACEX_PRICE;
    return total;
  };

  const handleSubmit = async () => {
    if (!vestDetails.trim() && !beltDetails.trim()) {
      alert('Please describe the revisions you need for at least one item.');
      return;
    }

    setSubmitting(true);

    try {
      await addDoc(collection(db, 'requests'), {
        userId: user.uid,
        userEmail: userData?.email || '',
        username: userData?.username || userData?.serverNickname || '',
        type: 'Revision - Vest',
        status: 'Unassigned',
        zap,
        discordName,
        paypalEmail,
        vestDetails,
        beltDetails,
        revisionDetails: `Vest: ${vestDetails}\n\nBelt: ${beltDetails}`,
        premiumAsset,
        customAcex,
        totalPrice: calculateTotal(),
        createdAt: Timestamp.now()
      });

      setSubmitSuccess(true);
    } catch (error) {
      console.error('Error submitting request:', error);
      alert('Error submitting request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitSuccess) {
    return (
      <div className="commission-page" style={{ backgroundImage: `url(/images/new_build_background_photo.png)`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'fixed' }}>
        <nav className="commission-navbar">
          <Link to="/" className="nav-left">
            <img src={unitLogo} alt="Logo" className="unit-logo" />
            <h1 className="site-title">Creature Supply</h1>
          </Link>
        </nav>
        <div className="success-container">
          <div className="success-card">
            <div className="success-icon">✓</div>
            <h2>Revision Request Submitted!</h2>
            <p>Your vest revision request has been received.</p>
            <p className="success-details">ZAP: {zap}</p>
            <p className="success-details">Total: ${calculateTotal()}</p>
            <p className="success-details">We'll notify you when your revision is ready.</p>
            <Link to="/" className="btn-home">Return Home</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="commission-page" style={{ backgroundImage: `url(/images/new_build_background_photo.png)`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'fixed' }}>
      {/* Navigation */}
      <nav className="commission-navbar">
        <Link to="/" className="nav-left">
          <img src={unitLogo} alt="Logo" className="unit-logo" />
          <h1 className="site-title">Creature Supply</h1>
        </Link>
        <div className="nav-right">
          <Link to="/" className="nav-link">Home</Link>
        </div>
      </nav>

      {/* Info Popup */}
      {showInfoPopup && (
        <div className="popup-overlay">
          <div className="popup-card">
            <form onSubmit={handleInfoSubmit}>
              <div className="popup-field">
                <label>Your ZAP Number</label>
                <input 
                  type="text" 
                  value={zap}
                  onChange={(e) => setZap(e.target.value)}
                  placeholder="Enter your ZAP"
                  required
                />
              </div>
              <div className="popup-field">
                <label>Discord Name</label>
                <input 
                  type="text" 
                  value={discordName}
                  onChange={(e) => setDiscordName(e.target.value)}
                  placeholder="J. Smith"
                  required
                />
              </div>
              <div className="popup-field">
                <label>PayPal Email</label>
                <input 
                  type="email" 
                  value={paypalEmail}
                  onChange={(e) => setPaypalEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                />
              </div>
              <button type="submit" className="popup-btn">Continue</button>
            </form>
          </div>
        </div>
      )}

      {/* Main Content */}
      {!showInfoPopup && (
        <div className="commission-content">
          <div className="commission-layout">
            {/* Left Side - Revision Details */}
            <div className="selection-panel">
              <h2 className="panel-title">Modify Existing Vest & Belt</h2>
              
              {/* Revision Details */}
              <div className="dropdown-section">
                <div className="dropdown-header">Revision Details</div>
                <p className="revision-instruction">Please be as specific as possible</p>
                
                <div className="revision-field">
                  <label className="revision-label">Vest:</label>
                  <textarea 
                    value={vestDetails}
                    onChange={(e) => setVestDetails(e.target.value)}
                    placeholder="Describe the changes you need to your vest configuration..."
                    className="revision-textarea"
                    rows={6}
                  />
                </div>

                <div className="revision-field">
                  <label className="revision-label">Belt:</label>
                  <textarea 
                    value={beltDetails}
                    onChange={(e) => setBeltDetails(e.target.value)}
                    placeholder="Describe the changes you need to your belt configuration..."
                    className="revision-textarea"
                    rows={6}
                  />
                </div>
              </div>

              {/* Premium Options */}
              <div className="dropdown-section">
                <div className="dropdown-header">Additional Options</div>
                <div className="premium-options">
                  <label className="checkbox-option">
                    <input 
                      type="checkbox"
                      checked={premiumAsset}
                      onChange={(e) => setPremiumAsset(e.target.checked)}
                    />
                    <span className="checkbox-label">Premium Asset</span>
                    <span className="checkbox-price">+$10</span>
                  </label>
                  <label className="checkbox-option">
                    <input 
                      type="checkbox"
                      checked={customAcex}
                      onChange={(e) => setCustomAcex(e.target.checked)}
                    />
                    <span className="checkbox-label">Custom ACEX Options</span>
                    <span className="checkbox-price">+$10</span>
                  </label>
                </div>
              </div>
            </div>

            {/* Right Side - Cart and Submit */}
            <div className="preview-panel">
              <div className="preview-header">Vest & Belt Revision</div>
              
              <div className="preview-box">
                <div className="preview-placeholder">
                  <span>Vest & Belt Modification Request</span>
                </div>
              </div>

              {/* Cart Section */}
              <div className="cart-section">
                <h3 className="cart-title">Cart</h3>
                
                <div className="cart-items">
                  <div className="cart-line">
                    <span>Vest & Belt Revision (Base)</span>
                    <span>${BASE_PRICE}</span>
                  </div>
                </div>

                {premiumAsset && (
                  <div className="cart-line addon">
                    <span>Premium Asset</span>
                    <span>+${PREMIUM_ASSET_PRICE}</span>
                  </div>
                )}

                {customAcex && (
                  <div className="cart-line addon">
                    <span>Custom ACEX</span>
                    <span>+${ACEX_PRICE}</span>
                  </div>
                )}

                <div className="cart-total">
                  <span>Total</span>
                  <span>${calculateTotal()}</span>
                </div>
              </div>

              <button 
                className="submit-btn"
                onClick={handleSubmit}
                disabled={submitting || (!vestDetails.trim() && !beltDetails.trim())}
              >
                {submitting ? 'Submitting...' : 'Submit Request'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default VestRevision;
